import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEtsyIntegration } from '@/hooks/useEtsyIntegration';
import { Store, Link, Unlink, RefreshCw, ExternalLink, Package } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

export const EtsyIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    connection,
    listings,
    loading,
    connecting,
    initiateConnect,
    completeConnect,
    disconnect,
    fetchListings,
  } = useEtsyIntegration();
  const [loadingListings, setLoadingListings] = useState(false);

  // Handle OAuth callback
  useEffect(() => {
    const isCallback = searchParams.get('etsy_callback');
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (isCallback && code && state) {
      completeConnect(code, state).then(() => {
        // Clean up URL params
        searchParams.delete('etsy_callback');
        searchParams.delete('code');
        searchParams.delete('state');
        setSearchParams(searchParams);
      });
    }
  }, [searchParams, completeConnect, setSearchParams]);

  const handleFetchListings = async () => {
    setLoadingListings(true);
    await fetchListings();
    setLoadingListings(false);
  };

  if (loading) {
    return (
      <Card className="border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Store className="h-5 w-5 text-primary" />
          Etsy Integration
        </CardTitle>
        <CardDescription>
          Connect your Etsy shop to upload AI-generated images directly to your listings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {connection ? (
          <>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Store className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{connection.shop_name || 'Connected Shop'}</p>
                  <p className="text-sm text-muted-foreground">
                    Connected {new Date(connection.connected_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Badge variant="secondary" className="bg-green-500/10 text-green-500">
                Connected
              </Badge>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleFetchListings}
                disabled={loadingListings}
              >
                {loadingListings ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Package className="h-4 w-4 mr-2" />
                )}
                Load Listings
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`https://www.etsy.com/shop/${connection.shop_name}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Shop
              </Button>
              <Button variant="destructive" onClick={disconnect}>
                <Unlink className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>

            {listings.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Your Listings ({listings.length})</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {listings.map((listing) => (
                    <div
                      key={listing.listing_id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{listing.title}</p>
                        <p className="text-sm text-muted-foreground">
                          ${(listing.price.amount / listing.price.divisor).toFixed(2)} · 
                          Qty: {listing.quantity}
                        </p>
                      </div>
                      <Badge variant={listing.state === 'active' ? 'default' : 'secondary'}>
                        {listing.state}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Connect your Etsy account to start uploading images to your listings
            </p>
            <Button onClick={initiateConnect} disabled={connecting}>
              {connecting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link className="h-4 w-4 mr-2" />
              )}
              Connect Etsy Account
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
